import { area, length } from '@natural-apptitude/geodesic';
import {
  buildCombinedCode,
  conditionAssessment,
  conditionToScore,
  denormalizeRecord,
  mapBaselineRawData,
  ukHabToMetric,
  UkhabUnknownSchemaError,
  type ConditionId,
  type CoreoRecordInput,
  type UkhabRecordSchemaId,
} from '@natural-apptitude/ukhab-lib';
import type { Geometry } from 'geojson';
import habitatNames from './ukhab-habitat-names.json';

const habitatNameByCode = habitatNames as Record<string, string>;

const isGeometry = (value: unknown): value is Geometry => {
  return value != null && typeof value === 'object' && 'type' in value;
};

export const geometryAreaHa = (geometryArg: unknown): number | undefined => {
  if (!isGeometry(geometryArg)) {
    return undefined;
  }
  if (geometryArg.type !== 'Polygon' && geometryArg.type !== 'MultiPolygon') {
    return undefined;
  }
  try {
    return area(geometryArg, { unit: 'ha' });
  } catch {
    return undefined;
  }
};

export const geometryLengthKm = (geometryArg: unknown): number | undefined => {
  if (!isGeometry(geometryArg)) {
    return undefined;
  }
  if (geometryArg.type !== 'LineString' && geometryArg.type !== 'MultiLineString') {
    return undefined;
  }
  try {
    return length(
      { type: 'Feature', properties: {}, geometry: geometryArg },
      { unit: 'km' },
    );
  } catch {
    return undefined;
  }
};

export const habitatName = (codeArg: unknown): string | undefined => {
  if (typeof codeArg !== 'string' || !codeArg.trim()) {
    return undefined;
  }
  return habitatNameByCode[codeArg] ?? undefined;
};

const toRecordInput = (value: unknown): CoreoRecordInput | null => {
  if (value == null || typeof value !== 'object' || !('data' in value)) {
    return null;
  }
  const record = value as CoreoRecordInput & {
    id?: number | null;
    projectId?: number;
    isNew?: boolean;
    parentId?: number | null;
    geometry?: unknown;
  };
  // Preserve host snapshot fields for forward-compat (ukhab-lib currently uses data/surveyId).
  return {
    data: record.data ?? {},
    surveyId: record.surveyId,
    id: record.id,
    projectId: record.projectId,
    isNew: record.isNew,
    parentId: record.parentId,
    geometry: record.geometry,
  } as CoreoRecordInput;
};

export const ukhabDenormalizeRecord = (
  recordArg: unknown,
  schemaIdArg: unknown,
): ReturnType<typeof denormalizeRecord> | null => {
  const record = toRecordInput(recordArg);
  if (!record || typeof schemaIdArg !== 'string') {
    return null;
  }
  try {
    return denormalizeRecord(record, schemaIdArg as UkhabRecordSchemaId);
  } catch (err) {
    if (err instanceof UkhabUnknownSchemaError) {
      console.warn('UKHab denormalize failed: unknown schema', {
        schemaId: schemaIdArg,
        surveyId: record.surveyId,
      });
      return null;
    }
    throw err;
  }
};

const METRIC_CHOICE_PATH = 'metChoice';

export const ukhabMapBaselineRawData = (dataArg: unknown): Record<string, unknown> => {
  if (dataArg == null || typeof dataArg !== 'object') {
    return mapBaselineRawData({});
  }
  const data = dataArg as Record<string, unknown>;
  const mapped = mapBaselineRawData(data);
  const metChoice = data[METRIC_CHOICE_PATH];
  if (metChoice != null && metChoice !== '') {
    mapped['metChoice'] = metChoice;
  }
  return mapped;
};

export const ukhabBuildCombinedCode = (mappedArg: unknown): string => {
  if (mappedArg == null || typeof mappedArg !== 'object') {
    return buildCombinedCode({});
  }
  return buildCombinedCode(mappedArg as Record<string, unknown>);
};

export const ukhabUkHabToMetric = (searchKeyArg: unknown) => {
  if (typeof searchKeyArg !== 'string') {
    return undefined;
  }
  const trimmed = searchKeyArg.trim();
  if (!trimmed) {
    return undefined;
  }
  return ukHabToMetric(trimmed);
};

export const ukhabConditionAssessment = (inputArg: unknown) => {
  if (inputArg == null || typeof inputArg !== 'object') {
    return undefined;
  }
  return conditionAssessment(inputArg as Parameters<typeof conditionAssessment>[0]);
};

export const ukhabConditionToScore = (conditionArg: unknown): number | undefined => {
  if (typeof conditionArg !== 'string') {
    return undefined;
  }
  return conditionToScore(conditionArg as ConditionId);
};
